import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
} from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';
import { Checkbox } from '../../ui/checkbox';
import type { Account, SimpleAccount } from '@/lib/types';

interface AccountTableProps {
  accounts: SimpleAccount[];
  onAccountClick?: (accountId: string) => void;
  favoriteAccountIds?: string[];
  showFavoritesOnly?: boolean;
  onShowFavoritesOnlyChange?: (show: boolean) => void;
  onToggleFavorite?: (accountId: string, isFavorite: boolean) => void;
}

export function AccountTable({
  accounts,
  onAccountClick,
  favoriteAccountIds = [],
  showFavoritesOnly = false,
  onShowFavoritesOnlyChange,
  onToggleFavorite,
}: AccountTableProps) {
  const handleRowClick = (accountId: string) => {
    onAccountClick?.(accountId);
  };

  const handleFavoriteClick = (event: React.MouseEvent, accountId: string) => {
    event.stopPropagation(); // テーブル行のクリックイベントを防止
    const isFavorite = favoriteAccountIds.includes(accountId);
    onToggleFavorite?.(accountId, !isFavorite);
  };

  // フィルタリング済みのアカウントリストを取得
  const displayedAccounts = showFavoritesOnly
    ? accounts.filter((account) => favoriteAccountIds.includes(account.id))
    : accounts;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '未設定';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ja-JP');
    } catch {
      return dateString;
    }
  };

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      {/* フィルター制御 */}
      <Box sx={{ p: 2 }}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="show-favorites-only"
            checked={showFavoritesOnly}
            onCheckedChange={(checked) => onShowFavoritesOnlyChange?.(checked === true)}
          />
          <label
            htmlFor="show-favorites-only"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            お気に入りのみ表示
          </label>
        </div>
      </Box>

      <TableContainer
        sx={{
          overflowX: 'auto',
          maxWidth: '100%',
          '& .MuiTable-root': {
            minWidth: 650,
          },
        }}
      >
        <Table sx={{ minWidth: 650, whiteSpace: 'nowrap' }} aria-label="アカウントテーブル">
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}></TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>ID</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>コード</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>アカウント名</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>基準日</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedAccounts.map((account, index) => {
              const isFavorite = favoriteAccountIds.includes(account.id);
              return (
                <TableRow
                  hover
                  key={`${account.id}-${index}`}
                  onClick={() => handleRowClick(account.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton
                      onClick={(e) => handleFavoriteClick(e, account.id)}
                      sx={{
                        color: isFavorite ? '#FFD700' : 'action.disabled',
                        '&:hover': {
                          color: '#FFD700',
                        },
                      }}
                    >
                      {isFavorite ? <Star /> : <StarBorder />}
                    </IconButton>
                  </TableCell>
                  <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                    {account.id}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{account.code}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{account.name}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(account.base_date)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
